import React from 'react';
import styled from '@emotion/styled';
import { colorToAlpha } from 'helpers/insight';
import { Text } from 'ds/components/typography';

export type Size = 'medium' | 'large' | 'extraLarge';

export const sizeToHeight = {
  medium: 32,
  large: 40,
  extraLarge: 48,
};

const WrapperClass = styled.div`
  position: relative;
  display: flex;
  margin: 0 -${({ theme }) => theme.spacing(0.5)};
  min-width: 0;
`;

const FilterStyleItem = styled(Text)<{active: boolean, isRound?: boolean, size?: Size, mode?: 'default' | 'black' }>`
  border-radius: ${({ theme }) => theme.borderRadius.smallestSideRound};
  color: ${props => props.theme.colors.neutrals.grey1};
  background-color: ${props => props.theme.colors.neutrals.white};
  cursor: pointer;
  letter-spacing: 0.5px;
  justify-content: center;
  outline: none;
  margin: 0 ${({ theme }) => theme.spacing(0.5)};
  min-height: ${({ size = 'large' }) => sizeToHeight[size]}px;
  min-width: ${({ size = 'large' }) => sizeToHeight[size]}px;
  border: 1px solid ${props => props.theme.colors.neutrals.grey6};
  margin-bottom: ${({ theme }) => theme.spacing(1)};
  display: flex;
  align-items: center;
  span {
    text-align: center;
  }

  ${({ theme, active, mode = 'default' }) => active ? `
    border: ${mode === 'black' ? `2px solid ${theme.colors.neutrals.grey1}` : `1px solid ${theme.colors.brand.lighter}`};
    background-color: ${mode === 'black' ? theme.colors.neutrals.grey10 : colorToAlpha(theme.colors.brand.lightest, 0.7)};
    color: ${mode === 'black' ? theme.colors.neutrals.grey1 : theme.colors.brand.main};
    ${mode === 'black' ? `
      > span:not(:last-of-type) {
        position: relative;
        &:after {
          content: '';
          position: absolute;
          height: 24px;
          width: 1px;
          background-color: ${theme.colors.neutrals.grey6};
          left: 0;
          top: 50%;
          transform: translateY(-50%);
        }
      }
    ` : ''}
  ` : `
    &:hover {
      border: 1px solid ${theme.colors.neutrals.grey4};
    }
  `}
`;

export interface ButtonSetProps {
  buttons: ButtonItem[];
}

export interface ButtonItem {
  label: React.ReactNode;
  click: (e: React.MouseEvent<any>) => void;
  active?: boolean;
  isRound?: boolean;
  size?: Size;
  mode?: 'default' | 'black';
}

export class ButtonSet extends React.Component<ButtonSetProps> {
  public renderItem(button: ButtonItem, index: number) {
    const { label, click, active, isRound, size, mode = 'default', ...divProps } = button;
    return (
      <FilterStyleItem
        mode={mode}
        key={index}
        active={active}
        onClick={click}
        isRound={isRound}
        size={size}
        {...divProps}
        data-auto={`filter-select-button-${label}`}
        data-auto-active={active}
      >
        {label}
      </FilterStyleItem>
    );
  }

  public render() {
    const { buttons } = this.props;

    return (
      <WrapperClass data-auto="selectButtonsRoot">
        {buttons.map(this.renderItem)}
      </WrapperClass>
    );
  }
}
